<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Create a Service Account</h1>
                    <p class="text-muted">Service accounts are used for API access and automation. They use API keys or tokens for authentication, not passwords.</p>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="!serviceAccountCreated" class="container">
            <div class="form-group">
                <label for="Email">Email:</label>
                <input v-model="email" type="email" class="form-control" placeholder="Email address for service account">
                <small class="form-text text-muted">This email will be used to identify the service account.</small>
                <ValidationError context="Email" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group">
                <label for="DisplayName">Display name:</label>
                <input v-model="displayName" type="text" class="form-control" placeholder="Display name for service account">
                <small class="form-text text-muted">A descriptive name for this service account (e.g., "CI/CD Pipeline", "Data Sync Service").</small>
                <ValidationError context="DisplayName" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group">
                <label for="Description">Description (optional):</label>
                <textarea v-model="description" class="form-control" rows="3" placeholder="Purpose of this service account"></textarea>
                <small class="form-text text-muted">Describe what this service account will be used for.</small>
            </div>

            <div class="alert alert-info" role="alert">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <strong>Note:</strong> Service accounts cannot sign in through the web UI and do not use passwords. They authenticate using API keys or access tokens which can be generated after creation.
            </div>

            <button v-show="statusInitial || statusInvalid" @click="handleCreateServiceUserCommandV1" type="button" class="btn btn-primary btn-lg">Create Service Account</button>
            <button v-show="statusInvoking" type="button" class="btn btn-primary btn-lg" disabled>Creating...</button>
        </div>

        <div v-else class="container">
            <div class="alert alert-success" role="alert">
                <h4 class="alert-heading">Service Account Created!</h4>
                <p>Service account <strong>{{email}}</strong> was created successfully.</p>
            </div>

            <h4 class="fg-color-blue">Next steps:</h4>
            <br />

            <a :href="`/#/admin/organizations/users/manage/type/${email}`" class="btn btn-success text-white" style="padding: 0.7em 2em;">
                <i class="fa fa-key" aria-hidden="true"></i>
                <span>Generate API keys</span>
            </a>

            <h4 class="fg-color-blue" style="margin: 20px 0;">or</h4>

            <a :href="`/#/admin/organizations/users/service-accounts/${tid}`" class="btn btn-secondary text-white" style="padding: 0.7em 2em;">
                <i class="fa fa-list" aria-hidden="true"></i>
                <span>View all service accounts</span>
            </a>

            <h4 class="fg-color-blue" style="margin: 20px 0;">or</h4>

            <button @click="handleFinish" class="btn btn-secondary text-white" style="padding: 0.7em 2em;">
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Create another service account</span>
            </button>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, mixins } from 'vue-facing-decorator';
    import PageBase from "@common/PageBase";
    import PageStatus from "@common/PageStatus";
    import Queries from "@Organizations/Users/queries-vue-component";
    import Commands from "@Organizations/Users/commands-vue-component";
    import "@common/AdminNavigationV2.vue";
    import "@common/Components/ValidationError.vue";

    @Component
    export default class CreateServiceAccountPage extends mixins(PageBase, PageStatus, Queries, Commands) {
        email = ''
        displayName = ''
        description = ''
        serviceAccountCreated = false

        mounted() {
            this.runCurrentUserQueryV1(false, false, true)
            this.setTitle('Create Service Account');
        }

        handleCreateServiceUserCommandV1() {
            this.$clearValidationErrors();
            this.setStatusInvoking();
            
            // Create service account with IsServiceUser = true and no user roles
            // No password is set - service accounts use API keys/tokens
            this.invokeCreateServiceUserCommandV1(
                '0', 
                this.tid, 
                this.email, 
                this.displayName, 
                this.description || 'Service Account'
            );
        }

        succeedCreateServiceUserCommandV1() {
            this.setStatusSucceed();
            this.serviceAccountCreated = true;
        }

        invalidCreateServiceUserCommandV1(status) {
            this.$pushValidationErrors(status.ValidationErrors);
            this.setStatusInvalid();
        }

        failedCreateServiceUserCommandV1(status) {
            this.setStatusFailed();
        }

        handleFinish() {
            this.email = '';
            this.displayName = '';
            this.description = '';
            this.serviceAccountCreated = false;
            this.setStatusInitial();
        }
    }
</script>